/**
 * Centralized authenticated fetch wrapper.
 *
 * - Adds the Bearer session_token from the store automatically
 * - On 401 (expired/invalid session): logs the user out, redirects to /login
 *   with `?reason=session_expired` so PageLogin can render a warning alert
 *   explaining what happened.
 *
 * 403 is intentionally NOT treated as session expiry — per RFC 7235, 403 means
 * the user IS authenticated but lacks permission for the resource. Logging out
 * a user who hits a permission-gated endpoint would be wrong.
 *
 * Pure logic. No UI imports. The Login page reads the URL query param
 * and renders the message itself.
 */
import store from '@/store'
import router from '@/router'

// Multiple in-flight calls can all 401 at once when the session expires
// (e.g. the Dashboard fires 5 parallel fetches). We only want to dispatch
// the logout + redirect once per "expiry event".
let sessionExpiredHandled = false

function handleSessionExpired () {
  if (sessionExpiredHandled) return
  sessionExpiredHandled = true

  store.dispatch('logout')
  router.push({ name: 'Login', query: { reason: 'session_expired' } })

  // Reset the guard after a moment so a fresh login session can later
  // trigger this flow again (e.g. user logs in, idles 24h, expires again).
  setTimeout(() => { sessionExpiredHandled = false }, 5000)
}

/**
 * fetch() wrapper that injects the Bearer session_token and handles
 * session expiry centrally.
 *
 * Usage:
 *   const res = await apiFetch(url, { method: 'POST', body: JSON.stringify(...) })
 *   if (res.status === 200) { ... }
 *
 * CONTRACT
 * --------
 * - HTTP errors (including 401) are NEVER thrown — they come back as a
 *   normal Response with the appropriate status code, just like native
 *   fetch. Callers branch on status as usual.
 * - 401 specifically is intercepted here (logout + redirect side-effect)
 *   *before* the response is returned, so callers don't have to do
 *   anything special; their existing `if (status === 200)` else branches
 *   fall through naturally while the user is being navigated to /login.
 * - The only case where apiFetch throws is the same case where native
 *   fetch throws: real network failures (offline, DNS failure, CORS
 *   preflight rejection, request abort). Callers that wrap apiFetch in
 *   try/catch are catching those network failures only.
 */
export async function apiFetch (url, options = {}) {
  const profile = store.getters.getUserProfile

  const headers = {
    'Accept': 'application/json',
    ...(options.headers || {}),
  }
  if (profile?.session_token) {
    headers['Authorization'] = `Bearer ${profile.session_token}`
  }

  const response = await fetch(url, {
    mode: 'cors',
    cache: 'no-cache',
    ...options,
    headers,
  })

  // Only treat 401 as an expired session if we *had* a token. A 401 from
  // POST /authenticate (login with wrong password) must NOT trigger the
  // expired-session flow — that's handled by PageLogin as a normal
  // credentials error. PageLogin uses raw fetch anyway, so this guard is
  // belt-and-suspenders for any future authenticated endpoint that could
  // also legitimately 401 a fresh request.
  if (response.status === 401 && profile?.session_token) {
    handleSessionExpired()
  }

  return response
}

/**
 * Best-effort server-side revocation of a session token (POST /logout),
 * so it dies immediately instead of lingering in the API's session store
 * until its 24h TTL runs out.
 *
 * Fire-and-forget by design: callers don't await it, and local logout
 * proceeds no matter what the server says (offline, token already
 * expired, 5xx). Deliberately raw fetch, NOT apiFetch — apiFetch would
 * intercept the 401 of an already-expired token and redirect to
 * /login?reason=session_expired, showing a misleading "session expired"
 * warning to a user who deliberately clicked Logout.
 */
export function revokeSessionToken (sessionToken) {
  if (!sessionToken) return

  fetch(process.env.VUE_APP_API_URL + '/logout', {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${sessionToken}`
    }
  }).catch(() => {}) // network failure — nothing to do, logout is local-first
}
